import React, { useState } from 'react';

import { Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useGetAnalyticsQuery } from '../api/analytics';
import { PageContent, PageHeader } from '../components/common';
import { readPreviewRole } from '../components/RoleSwitcher';
import { useFeature } from '../hooks';
import { useAuth } from '../providers/AppAuthProvider';
import { hasAnyRole } from '../utils/utils';

import type { AnalyticsRange, AnalyticsReport } from '../api/analytics';

// Same list as ANALYTICS_ROLES in api/statement_analyzer/urls/analytics_api.py.
const ANALYTICS_ROLES = ['admin', 'enterprise_admin', 'super_admin', 'manager'];
const RANGES: AnalyticsRange[] = [30, 7, 90];
const NOT_YET = '—';

const ISSUE_LABELS: Record<string, string> = {
	NOT_A_STATEMENT: 'Not a statement',
	MISSING_PAGES: 'Missing pages',
	UNREADABLE: 'Unreadable',
	PARTLY_READABLE: 'Partly readable',
	OTHER: 'Other problem',
};

const money = (n: number | null | undefined) =>
	n == null
		? NOT_YET
		: n.toLocaleString('en-US', {
				style: 'currency',
				currency: 'USD',
				maximumFractionDigits: 0,
			});

// v9 shows annual savings with cents, e.g. $14,912.76.
const money2 = (n: number) =>
	n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const duration = (seconds: number | null | undefined) => {
	if (seconds == null) return NOT_YET;
	const s = Math.round(seconds);
	const pad = (n: number) => String(n).padStart(2, '0');
	return s < 3600
		? `${Math.floor(s / 60)}m ${pad(s % 60)}s`
		: `${Math.floor(s / 3600)}h ${pad(Math.floor((s % 3600) / 60))}m`;
};

const percent = (part: number, whole: number) =>
	whole ? `${Math.round((part / whole) * 100)}%` : NOT_YET;

const day = (d: Date) =>
	d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

// v9 tooltip range, e.g. "31 Aug – 6 Sep" or "7 – 13 Sep".
const weekRange = (iso: string) => {
	const start = new Date(`${iso}T00:00:00`);
	const end = new Date(start);
	end.setDate(start.getDate() + 6);
	return start.getMonth() === end.getMonth()
		? `${start.getDate()} – ${day(end)}`
		: `${day(start)} – ${day(end)}`;
};

const csvCell = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;

export const analyticsCsv = (r: AnalyticsReport) =>
	[
		['Analytics', `Last ${r.days} days`],
		[],
		['Statements uploaded', r.totals.statements],
		['Statements processed', r.totals.processed],
		['Median time per statement (s)', r.totals.median_processing_seconds],
		['Average customer savings / month', r.totals.avg_monthly_savings],
		[],
		['Week starting', 'Statements'],
		...r.weeks.map((w) => [w.week_start, w.statements]),
		[],
		[
			'Rep',
			'Email',
			'Statements',
			'Processed',
			'Avg savings / month',
			'Median time (s)',
		],
		...r.reps.map((p) => [
			p.name,
			p.email,
			p.statements,
			p.processed,
			p.avg_monthly_savings,
			p.median_processing_seconds,
		]),
		[],
		['Statement', 'Rep', 'Monthly volume', 'Annual savings', 'Days open'],
		...r.opportunities.map((o) => [
			o.name,
			o.rep,
			o.monthly_volume,
			o.yearly_savings,
			o.days_open,
		]),
	]
		.map((row) => row.map(csvCell).join(','))
		.join('\n');

const downloadCsv = (report: AnalyticsReport) => {
	const url = URL.createObjectURL(
		new Blob([analyticsCsv(report)], { type: 'text/csv' }),
	);
	const link = document.createElement('a');
	link.href = url;
	link.download = `analytics-last-${report.days}-days.csv`;
	link.click();
	URL.revokeObjectURL(url);
};

// v9 `.card`, `.card-head`, `.card-title`, `.card-sub`.
const Card: React.FC<{
	title: string;
	sub: string;
	children: React.ReactNode;
}> = ({ title, sub, children }) => (
	<div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
		<div className="flex items-center justify-between gap-4 px-6 pt-6">
			<div>
				<div className="text-lg font-normal leading-normal text-gray-900">
					{title}
				</div>
				<div className="mt-0.5 text-sm text-gray-500">{sub}</div>
			</div>
		</div>
		{children}
	</div>
);

const Metric: React.FC<{
	label: string;
	value: string;
	note: string;
	good?: boolean;
}> = ({ label, value, note, good }) => (
	<div
		className={`rounded-lg border p-4 ${good ? 'border-green-100 bg-green-50' : 'border-gray-200 bg-white'}`}
	>
		<div className="text-xs text-gray-500">{label}</div>
		<div
			className={`mt-1 text-2xl font-semibold leading-tight tabular-nums ${good ? 'text-green-800' : 'text-gray-900'}`}
		>
			{value}
		</div>
		<div className="mt-1 text-xs text-gray-500">{note}</div>
	</div>
);

// v9 `table.compact` inside `.table-wrap.mt-4`.
const Table: React.FC<{
	columns: string[];
	rows: React.ReactNode[][];
	empty: string;
	onRowClick?: (index: number) => void;
}> = ({ columns, rows, empty, onRowClick }) => (
	<div className="mt-4 overflow-x-auto">
		<table className="w-full border-collapse">
			<thead>
				<tr>
					{columns.map((c) => (
						<th
							key={c}
							className="whitespace-nowrap border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-left text-xs font-medium uppercase tracking-[.05em] text-gray-500"
						>
							{c}
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{rows.length === 0 ? (
					<tr>
						<td
							colSpan={columns.length}
							className="px-4 py-2.5 text-sm text-gray-500"
						>
							{empty}
						</td>
					</tr>
				) : (
					rows.map((cells, i) => (
						<tr
							key={i}
							onClick={onRowClick && (() => onRowClick(i))}
							className={onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''}
						>
							{cells.map((cell, j) => (
								<td
									key={columns[j]}
									className={`whitespace-nowrap border-b border-gray-200 px-4 py-2.5 align-middle text-sm text-gray-900 [tr:last-child_&]:border-b-0 ${j === 0 ? 'font-medium' : ''}`}
								>
									{cell}
								</td>
							))}
						</tr>
					))
				)}
			</tbody>
		</table>
	</div>
);

// A metric we cannot compute yet: a dash, with the reason on hover.
const NotYet: React.FC<{ why: string }> = ({ why }) => (
	<span title={why} className="text-gray-500">
		{NOT_YET}
	</span>
);

// v9 `.bars`: one column per week, value and week number under the bar, tooltip on hover.
const WeekBars: React.FC<{ weeks: AnalyticsReport['weeks'] }> = ({ weeks }) => {
	const max = Math.max(1, ...weeks.map((w) => w.statements));
	return (
		<div className="group/bars flex h-[180px] items-end gap-3 pt-2">
			{weeks.map((w, i) => {
				const change = i ? w.statements - weeks[i - 1].statements : null;
				const tipSide =
					i === 0
						? 'left-0'
						: i === weeks.length - 1
							? 'right-0'
							: 'left-1/2 -translate-x-1/2';
				return (
					<div
						key={w.week_start}
						tabIndex={0}
						aria-label={`Week ${i + 1}, ${weekRange(w.week_start)}: ${w.statements} statements`}
						className="group/bar flex h-full flex-1 flex-col items-center justify-end gap-1.5 outline-none"
					>
						<div
							className="relative flex w-full items-end"
							style={{ height: `${Math.round((w.statements / max) * 120)}px` }}
						>
							<i className="block h-full min-h-[4px] w-full rounded-t-md bg-primary-400 transition-opacity group-hover/bars:opacity-40 group-focus-within/bars:opacity-40 group-hover/bar:!bg-primary-500 group-hover/bar:!opacity-100 group-focus-visible/bar:!bg-primary-500 group-focus-visible/bar:!opacity-100" />
							<div
								role="tooltip"
								className={`invisible pointer-events-none absolute bottom-[calc(100%+8px)] z-10 min-w-[148px] whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-xs font-normal leading-[18px] text-white shadow-[0_8px_20px_rgba(0,6,102,.18)] group-hover/bar:visible group-focus-visible/bar:visible ${tipSide}`}
							>
								<b className="block font-semibold">
									W{i + 1} · {weekRange(w.week_start)}
								</b>
								{w.statements} statements
								<br />
								{change === null ? (
									'First week in range'
								) : (
									<>
										<em
											className={`not-italic ${change < 0 ? 'text-sand-300' : 'text-green-300'}`}
										>
											{change > 0 ? '+' : ''}
											{change}
										</em>{' '}
										vs the week before
									</>
								)}
							</div>
						</div>
						<span className="text-xs font-semibold text-gray-900">
							{w.statements}
						</span>
						<small className="text-[11px] text-gray-500 group-focus-visible/bar:font-semibold group-focus-visible/bar:text-primary-500">
							W{i + 1}
						</small>
					</div>
				);
			})}
		</div>
	);
};

type Dot = 'ok' | 'warn' | 'cur';
const DOTS: Record<Dot, string> = {
	ok: 'bg-green-500',
	warn: 'bg-amber-500',
	cur: 'bg-primary-400',
};

const Report: React.FC<{ report: AnalyticsReport }> = ({ report }) => {
	const navigate = useNavigate();
	const { totals, insights } = report;
	const approvalsNote = 'available when the approval queue ships';

	const metrics = [
		{
			label: 'Statements processed',
			value: String(totals.processed),
			note: `${totals.statements} uploaded · ${totals.statements - totals.processed} not finished`,
		},
		{
			label: 'Median time per merchant',
			value: duration(totals.median_processing_seconds),
			note: 'per statement, from upload to first result',
		},
		{ label: 'Proposals approved', value: NOT_YET, note: approvalsNote },
		{
			label: 'Exceptions routed',
			value: NOT_YET,
			note: 'available when pricing limits ship',
		},
		{
			label: 'Average customer savings',
			value: money(totals.avg_monthly_savings),
			note:
				totals.avg_monthly_savings == null
					? 'no quoted statements yet'
					: `a month per statement · ${money(totals.avg_monthly_savings * 12)} a year`,
			good: true,
		},
		{
			label: 'Padding found',
			value: NOT_YET,
			note: 'available when padding detection ships',
		},
	];

	const insightRows: {
		dot: Dot;
		label: string;
		note: string;
		value: string;
	}[] = [
		{
			dot: 'ok',
			label: 'Discount rate padding',
			note: 'available when padding detection ships',
			value: NOT_YET,
		},
		{
			dot: 'ok',
			label: 'Non-qualified downgrades found',
			note: 'available when downgrade detection ships',
			value: NOT_YET,
		},
		{
			dot: 'warn',
			label: 'Pages that needed a person',
			note: insights.most_common_issue
				? `most common: ${ISSUE_LABELS[insights.most_common_issue] ?? insights.most_common_issue}`
				: 'flagged by the document check',
			value: percent(insights.flagged_statements, totals.statements),
		},
		{
			dot: 'cur',
			label: 'Most used template',
			note: 'available when pricing templates ship',
			value: NOT_YET,
		},
		{
			dot: 'cur',
			label: 'Most common exception',
			note: 'available when pricing limits ship',
			value: NOT_YET,
		},
		{
			dot: 'ok',
			label: `Quotes that save money in ${insights.statements_with_savings} of ${insights.quoted_statements} statements`,
			note: 'proposed fees below current fees',
			value: percent(
				insights.statements_with_savings,
				insights.quoted_statements,
			),
		},
		{
			dot: 'cur',
			label: 'Average effective rate',
			note: 'current pricing → proposed pricing',
			value:
				insights.avg_current_effective_rate == null
					? NOT_YET
					: `${insights.avg_current_effective_rate.toFixed(2)}% → ${insights.avg_proposed_effective_rate?.toFixed(2)}%`,
		},
	];

	return (
		<div className="space-y-4">
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
				{metrics.map((m) => (
					<Metric key={m.label} {...m} />
				))}
			</div>

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
				<Card title="Statements per week" sub="Emailed in and processed">
					<div className="p-6">
						<WeekBars weeks={report.weeks} />
					</div>
				</Card>
				<Card title="Statement insights" sub="What the analysis found">
					<ul className="m-0 list-none p-6">
						{insightRows.map((r) => (
							<li
								key={r.label}
								className="flex items-start gap-3 border-t border-gray-100 py-2.5 text-sm first:border-t-0 first:pt-0"
							>
								<span
									className={`mt-[7px] h-2 w-2 flex-none rounded-full ${DOTS[r.dot]}`}
								/>
								<div className="min-w-0 flex-1 text-gray-900">
									{r.label}
									<small className="block text-xs text-gray-500">
										{r.note}
									</small>
								</div>
								<span className="ml-auto whitespace-nowrap text-xs text-gray-500">
									{r.value}
								</span>
							</li>
						))}
					</ul>
				</Card>
			</div>

			<Card title="Team" sub={`By rep, last ${report.days} days`}>
				<Table
					columns={[
						'Rep',
						'Statements',
						'Quotes',
						'Outside limits',
						'Approved',
						'Avg customer savings',
						'Median time',
					]}
					rows={report.reps.map((r) => [
						r.name,
						r.statements,
						<NotYet key="q" why={approvalsNote} />,
						<NotYet key="o" why="available when pricing limits ship" />,
						<NotYet key="a" why={approvalsNote} />,
						r.avg_monthly_savings == null
							? NOT_YET
							: `${money(r.avg_monthly_savings)} / mo`,
						duration(r.median_processing_seconds),
					])}
					empty="No statements in this range."
				/>
			</Card>

			<Card
				title="By partner"
				sub="Who sends the statements, and what each partner earns from Schedule A"
			>
				<Table
					columns={[
						'Partner',
						'Statements',
						'Quotes',
						'Approved',
						'Expected partner revenue',
					]}
					rows={[]}
					empty="Available when partners ship: statements are not linked to a partner yet."
				/>
			</Card>

			<Card
				title="Prospect opportunity insights"
				sub="Open merchants by annual savings"
			>
				<Table
					columns={[
						'Merchant',
						'Rep',
						'Monthly volume',
						'Annual savings',
						'Stage',
						'Days open',
					]}
					rows={report.opportunities.map((o) => [
						o.name,
						o.rep,
						money(o.monthly_volume),
						money2(o.yearly_savings),
						<NotYet key="s" why="available when proposals ship" />,
						o.days_open,
					])}
					onRowClick={(i) =>
						navigate(`/analysis/${report.opportunities[i].statement_id}`)
					}
					empty="No quoted statements with savings in this range."
				/>
			</Card>
		</div>
	);
};

const AnalyticsPage: React.FC = () => {
	const { user } = useAuth();
	const previewing = useFeature('role_switcher');
	const [days, setDays] = useState<AnalyticsRange>(30);
	const allowed =
		hasAnyRole(user?.roles, ANALYTICS_ROLES) &&
		(!previewing || ['manager', 'admin'].includes(readPreviewRole()));
	const { data, isFetching, isError } = useGetAnalyticsQuery(days, {
		skip: !allowed,
	});

	return (
		<div className="flex flex-1 flex-col">
			<PageHeader>
				<div>
					<div className="text-xl font-normal leading-normal text-gray-900">
						Analytics
					</div>
					<div className="mt-1 max-w-[72ch] text-base leading-normal text-gray-500">
						Usage over the last {days} days
					</div>
				</div>
				<div className="flex flex-shrink-0 items-center gap-3">
					<select
						aria-label="Range"
						value={days}
						onChange={(e) => setDays(Number(e.target.value) as AnalyticsRange)}
						className="block w-[150px] rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm leading-5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
					>
						{RANGES.map((d) => (
							<option key={d} value={d}>
								Last {d} days
							</option>
						))}
					</select>
					<button
						type="button"
						disabled={!allowed || !data}
						onClick={() => data && downloadCsv(data)}
						className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium leading-5 text-primary-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
					>
						<Download className="h-4 w-4 flex-none" />
						Export
					</button>
				</div>
			</PageHeader>
			<PageContent>
				{!allowed ? (
					<div className="rounded-lg border border-gray-200 bg-white p-6">
						<div className="text-base font-medium text-gray-900">
							Analytics opens for managers and admins
						</div>
					</div>
				) : isError ? (
					<div className="rounded-lg border border-red-100 bg-red-50 p-6 text-sm text-red-700">
						Analytics could not be loaded. Try again in a moment.
					</div>
				) : !data || isFetching ? (
					<div className="text-sm text-gray-500">Loading analytics…</div>
				) : (
					<Report report={data} />
				)}
			</PageContent>
		</div>
	);
};

export default AnalyticsPage;
